import useObservationMapping from "./useObservationMapping";
import useObservationVisibility from "./useObservationVisibility";
import {
  RISK,
  NOTES,
  COMPLIANCE,
  CASUAL_FACTOR,
  RECOMMENDATION,
  IS_INAPPLICABLE,
  CONFORMITY_LEVEL,
  CORRECTIVE_ACTION,
  COMPLIANCE_INAPPLICABLE_REASONS,
} from "features/assessment/constants/observationProps";

const useObservationValidation = () => {
  const {
    showRisk,
    showNotes,
    showCompliance,
    showCasualFactor,
    showRecommendation,
    showConformityLevel,
    showCorrectiveAction,
    showConformityInapplicableReasons,
  } = useObservationVisibility();

  const { complianceMap } = useObservationMapping();

  return [
    {
      id: RISK,
      showField: showRisk,
      property: "risk",
      fieldType: "Radio Field",
      label: function (options = []) {
        return options.find(({ id }) => id === this.id)?.name ?? "No Label";
      },
      riskLevels: [],
      answerMapping: function (item) {
        const num = parseInt(item);
        const riskLevels = this.riskLevels ?? [];
        const _risk = Array.isArray(riskLevels)
          ? riskLevels.find(({ id }) => parseInt(id) === num)
          : undefined;
        if (_risk) return _risk?.value;
        if (num === 0) return "Low";
        if (num === 1) return "Medium";
        if (num === 2) return "High";
        return "";
      },
      isValid: function (args) {
        const risk = parseInt(args?.risk);
        return !isNaN(risk);
      },
    },
    {
      id: COMPLIANCE,
      showField: showCompliance,
      property: "compliance",
      fieldType: "Radio Field",
      label: function (options = []) {
        return options.find(({ id }) => id === this.id)?.name ?? "No Label";
      },
      answerMapping: complianceMap,
      isValid: function (args) {
        const compliance = parseInt(args?.compliance);
        return !isNaN(compliance);
      },
    },
    {
      id: CONFORMITY_LEVEL,
      showField: showConformityLevel,
      property: "conformityLevel",
      fieldType: "Radio Field",
      conformity: [],
      answerMapping: function conformityLevelMap(item) {
        const num = parseInt(item);
        const conformity = this.conformity ?? [];
        const _conformity = Array.isArray(conformity)
          ? conformity.find(({ id }) => parseInt(id) === num)
          : undefined;
        if (_conformity) return _conformity?.value;

        if (num === 0) return "Fully Meets";
        if (num === 1) return "Substantial Conformity";
        if (num === 2) return "Partial Conformity";
        if (num === 3) return "Does Not Meet";
        if (num === 4) return "Not Applicable";
        return "";
      },
      label: function (options = []) {
        return options.find(({ id }) => id === this.id)?.name ?? "No Label";
      },
      isValid: function (args) {
        const conformityLevel = parseInt(args?.conformityLevel);
        if (!isNaN(conformityLevel) && conformityLevel !== 4) return true;
        if (conformityLevel === 4 && !!args?.conformityInapplicableReasons)
          return true;
        return false;
      },
    },
    {
      id: NOTES,
      showField: showNotes,
      property: "notes",
      fieldType: "Text Field",
      label: function (options = []) {
        return options.find(({ id }) => id === this.id)?.name ?? "No Label";
      },
      answerMapping: (data) => data,
      isValid: (args) => !!args?.notes,
    },
    {
      id: RECOMMENDATION,
      showField: showRecommendation,
      property: "recommendation",
      fieldType: "Text Field",
      label: function (options = []) {
        return options.find(({ id }) => id === this.id)?.name ?? "No Label";
      },
      answerMapping: (data) => data,
      isValid: () => true,
    },
    {
      id: CASUAL_FACTOR,
      showField: showCasualFactor,
      property: "causalFactors",
      fieldType: "Text Field",
      label: function (options = []) {
        return options.find(({ id }) => id === this.id)?.name ?? "No Label";
      },
      answerMapping: (data) => data,
      isValid: () => true,
    },
    {
      id: CORRECTIVE_ACTION,
      showField: showCorrectiveAction,
      property: "correctiveAction",
      fieldType: "Text Field",
      label: function (options = []) {
        return options.find(({ id }) => id === this.id)?.name ?? "No Label";
      },
      answerMapping: (data) => data,
      isValid: function (args) {
        const compliance = parseInt(args?.compliance);
        if (args?.correctiveAction === null) return true;
        if (isNaN(compliance)) return false;
        if (compliance === 1) return true;
        if (compliance === 0 && !!args?.correctiveAction) return true;
        return false;
      },
    },
    {
      id: COMPLIANCE_INAPPLICABLE_REASONS,
      showField: showConformityInapplicableReasons,
      property: "conformityInapplicableReasons",
      fieldType: "Text Field",
      label: function (options = []) {
        return (
          options.find(({ id }) => id === this.id)?.name ??
          "Why is conformity not applicable?"
        );
      },
      answerMapping: (data) => data,
      isValid: () => true,
    },
    {
      id: IS_INAPPLICABLE,
      showField: () => true,
      property: "isInapplicable",
      fieldType: "Radio Field",
      label: function (options = []) {
        return (
          options.find(({ id }) => id === this.id)?.name ??
          "Question does not apply to this audit"
        );
      },
      answerMapping: (item) => {
        const num = parseInt(item);
        if (num === 0) return "No";
        if (num === 1) return "Yes";
        if (item) return "Yes";
        return "No";
      },
      isValid: () => true,
    },
  ];
};

export default useObservationValidation;
